import { useEffect, useState } from "react";
import SiteHeader from "@/components/SiteHeader";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Quote, Linkedin } from "lucide-react";

function EndorsementCard({ e, large = false }) {
  return (
    <div
      data-testid={`endorsement-${e.id}`}
      className={`relative bg-white border ${e.featured ? "border-gold" : "border-navy/10"} p-6 ${large ? "md:p-10" : ""}`}
    >
      <Quote className={`absolute -top-3 left-6 bg-cream px-2 ${e.featured ? "text-gold" : "text-navy/30"} h-7 w-7`} />
      <p className={`text-navy/85 font-light leading-relaxed italic font-serif ${large ? "text-xl md:text-2xl" : "text-base"}`}>
        “{e.quote}”
      </p>
      <div className="mt-6 flex items-center gap-4">
        {e.headshot_url ? (
          <img
            src={e.headshot_url}
            alt={e.name}
            referrerPolicy="no-referrer"
            className="h-12 w-12 rounded-full object-cover border border-navy/10"
            onError={(ev) => { ev.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="h-12 w-12 rounded-full bg-navy text-gold flex items-center justify-center font-serif text-lg">
            {(e.name || "?").slice(0, 1)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-navy">{e.name}</div>
          <div className="text-xs text-navy/60 truncate">
            {[e.role, e.organization].filter(Boolean).join(" · ")}
          </div>
        </div>
        {e.linkedin_url && (
          <a
            href={e.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`endorsement-linkedin-${e.id}`}
            className="text-navy/40 hover:text-gold-deep"
          >
            <Linkedin className="h-4 w-4" />
          </a>
        )}
      </div>
      {e.featured && (
        <div className="absolute top-3 right-3 text-[10px] uppercase tracking-widest bg-gold text-navy px-2 py-0.5">
          Featured
        </div>
      )}
    </div>
  );
}

export default function Endorsements() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/public/endorsements").then((r) => setItems(r.data));
  }, []);

  const featured = items.filter((i) => i.featured);
  const rest = items.filter((i) => !i.featured);

  return (
    <div className="bg-cream min-h-screen text-navy" data-testid="endorsements-page">
      <SiteHeader />

      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-12">
        <div className="overline text-gold-deep mb-4">Endorsements</div>
        <h1 className="editorial-h1 text-5xl md:text-6xl text-navy">
          In <span className="italic">their</span> words.
        </h1>
        <div className="gold-rule mt-6 mb-8" />
        <p className="text-navy/75 max-w-2xl font-light leading-relaxed">
          A few words from peers, portfolio CEOs, co-investors and partners I've worked alongside across Sub-Saharan
          Africa and emerging markets.
        </p>
        <div className="mt-6">
          <Link
            to="/endorse"
            data-testid="endorsements-leave-link"
            className="inline-block text-xs uppercase tracking-widest border border-navy text-navy px-5 py-2.5 hover:bg-navy hover:text-gold transition-colors"
          >
            Leave an endorsement →
          </Link>
        </div>
      </section>

      {featured.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 md:px-10 pb-12" data-testid="endorsements-featured">
          <div className="overline text-gold-deep mb-4">Featured</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {featured.map((e) => <EndorsementCard key={e.id} e={e} large />)}
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24">
        {rest.length > 0 && (
          <>
            <div className="overline text-gold-deep mb-4 mt-6">All endorsements</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="endorsements-grid">
              {rest.map((e) => <EndorsementCard key={e.id} e={e} />)}
            </div>
          </>
        )}
        {items.length === 0 && (
          <div
            data-testid="endorsements-empty-state"
            className="mt-8 bg-white border border-navy/10 px-6 md:px-12 py-16 md:py-20"
          >
            <div className="max-w-2xl mx-auto text-center">
              {/* Editorial mark — concentric quote marks in gold/navy */}
              <div
                aria-hidden="true"
                className="relative mx-auto h-32 w-32 mb-8"
              >
                <div className="absolute inset-0 rounded-full border border-gold/40" />
                <div className="absolute inset-3 rounded-full border border-navy/15" />
                <div className="absolute inset-7 rounded-full bg-gradient-to-br from-gold/15 to-cream" />
                <Quote className="absolute inset-0 m-auto h-10 w-10 text-gold-deep" strokeWidth={1.5} />
                <Quote
                  className="absolute inset-0 m-auto h-10 w-10 text-gold-deep/40 rotate-180 translate-y-3 translate-x-3"
                  strokeWidth={1.5}
                />
              </div>

              <div className="overline text-gold-deep mb-3">Awaiting first voices</div>
              <h2 className="font-serif text-3xl md:text-4xl text-navy leading-tight">
                The wall is <span className="italic">quiet</span> — for now.
              </h2>
              <div className="gold-rule mx-auto mt-6 mb-6" />
              <p className="text-navy/70 font-light leading-relaxed">
                Endorsements appear here once peers, portfolio CEOs and co-investors share a few words
                about working together. If you've collaborated with Joseph, leaving an endorsement only
                takes a minute.
              </p>
              <Link
                to="/endorse"
                data-testid="endorsements-empty-cta"
                className="inline-block mt-8 text-xs uppercase tracking-widest bg-navy text-gold px-6 py-3 hover:bg-navy-600 transition-colors"
              >
                Be the first to endorse →
              </Link>
            </div>
          </div>
        )}
      </section>

      <footer className="bg-navy text-cream/70 py-12">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-xs flex justify-between">
          <span>© {new Date().getFullYear()} Joseph Poche</span>
          <Link to="/" className="text-gold hover:underline">Back to site</Link>
        </div>
      </footer>
    </div>
  );
}
